<template>
  <div class="app-container pb-50px">
    <el-card v-loading="loading" shadow="never">
      <el-alert
        class="mb-16px"
        type="info"
        :closable="false"
        show-icon
        title="全站排名按自然月执行"
        description="调度器每5分钟检查一次是否到期，但每个自然月只创建一个付费Cycle；若服务在计划日期后恢复且本月尚未执行，会立即补跑本月。"
      ></el-alert>
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        :disabled="!canEdit"
        size="small"
        label-width="150px"
      >
        <el-form-item label="启用 DataForSEO" prop="enabled">
          <el-switch
            v-model="form.enabled"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="停用"
          ></el-switch>
        </el-form-item>
        <el-form-item label="API Login" prop="login">
          <el-input
            v-model="form.login"
            style="width: 480px"
            placeholder="请输入 DataForSEO Login"
          ></el-input>
        </el-form-item>
        <el-form-item label="API Password" prop="password">
          <el-input
            v-model="form.password"
            type="password"
            show-password
            style="width: 480px"
            placeholder="留空则不修改已保存密码"
          ></el-input>
          <div class="form-tips">
            当前状态：{{
              form.password_configured ? "已配置" : "未配置"
            }}；环境变量：{{
              form.env_configured ? "已配置" : "未配置"
            }}。密码加密保存，不回显明文。
          </div>
        </el-form-item>
        <el-form-item label="排名查询日期" prop="monthly_refresh_day">
          <span class="form-prefix">每月</span>
          <el-input-number
            v-model="form.monthly_refresh_day"
            :min="1"
            :max="28"
            :step="1"
            :precision="0"
          ></el-input-number>
          <span class="form-unit">日</span>
          <div class="form-tips">
            每个自然月在指定日期创建全站 SEO 排名查询
            Cycle。限制1～28日，避免部分月份不存在29～31日。
          </div>
        </el-form-item>
        <el-form-item label="排名查询时间" prop="monthly_refresh_time">
          <el-time-picker
            v-model="form.monthly_refresh_time"
            format="HH:mm"
            value-format="HH:mm"
            placeholder="选择时间"
          ></el-time-picker>
          <div class="form-tips">
            按北京时间（Asia/Shanghai）启动。这是 Cycle
            开始时间，不是全部排名的预计完成时间。修改后只对尚未创建的下一期
            Cycle 生效。
          </div>
        </el-form-item>
        <el-form-item label="执行时区">
          <el-tag size="small" type="info">{{ form.monthly_timezone }}</el-tag>
        </el-form-item>
        <el-form-item label="自动查询模式">
          <el-tag size="small" type="success">Standard 异步</el-tag>
          <div class="form-tips">
            全站月度 Cycle 和指定站点手动同步固定使用 Standard 异步队列。
          </div>
        </el-form-item>
        <el-form-item label="查询深度模式" prop="rank_depth_mode">
          <el-radio-group v-model="form.rank_depth_mode">
            <el-radio-button label="default">使用默认查询深度</el-radio-button>
            <el-radio-button label="by_keyword_type"
              >自定义查询深度</el-radio-button
            >
          </el-radio-group>
        </el-form-item>
        <el-form-item
          v-if="form.rank_depth_mode === 'default'"
          label="默认查询深度"
          prop="rank_check_depth"
        >
          <el-input-number
            v-model="form.rank_check_depth"
            :min="1"
            :max="700"
            :step="10"
            :precision="0"
          ></el-input-number>
          <div class="form-tips">
            B2B关键词建议100，30可能只覆盖前三页附近；当前默认50用于兼顾性价比。
          </div>
        </el-form-item>
        <template v-else>
          <el-form-item label="主词深度" prop="rank_depth_core">
            <el-input-number
              v-model="form.rank_depth_core"
              :min="1"
              :max="700"
              :step="10"
              :precision="0"
            ></el-input-number>
            <span class="form-unit">推荐100</span>
          </el-form-item>
          <el-form-item label="长尾词深度" prop="rank_depth_long_tail">
            <el-input-number
              v-model="form.rank_depth_long_tail"
              :min="1"
              :max="700"
              :step="10"
              :precision="0"
            ></el-input-number>
            <span class="form-unit">推荐50</span>
          </el-form-item>
          <el-form-item label="品牌词深度" prop="rank_depth_brand">
            <el-input-number
              v-model="form.rank_depth_brand"
              :min="1"
              :max="700"
              :step="10"
              :precision="0"
            ></el-input-number>
            <span class="form-unit">推荐30</span>
          </el-form-item>
          <el-form-item label="竞品词深度" prop="rank_depth_competitor">
            <el-input-number
              v-model="form.rank_depth_competitor"
              :min="1"
              :max="700"
              :step="10"
              :precision="0"
            ></el-input-number>
            <span class="form-unit">推荐100</span>
            <div class="form-tips">
              保存后会按关键词类型同步写入现有关键词的查询深度。
            </div>
          </el-form-item>
        </template>
        <el-form-item label="Standard批量数" prop="standard_batch_size">
          <el-input-number
            v-model="form.standard_batch_size"
            :min="100"
            :max="100"
            :step="100"
            :precision="0"
            disabled
          ></el-input-number>
          <div class="form-tips">
            Cycle固定每批100个任务；不足100的尾批与其他站点余数合并，不再提供可变批量设置。
          </div>
        </el-form-item>
        <el-form-item label="Live手动诊断" prop="live_manual_enabled">
          <el-switch
            v-model="form.live_manual_enabled"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="停用"
          ></el-switch>
          <div class="form-tips">
            Live实时接口仅用于管理员单关键词人工核验，不参与自动批量查询。
          </div>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="mt-16px" shadow="never">
      <template #header>
        <span>DataForSEO 操作</span>
      </template>
      <div class="action-row">
        <el-button
          v-if="$hasPermission('seo.setting', 'edit')"
          size="small"
          :loading="actionLoading.test"
          @click="onTestConnection"
        >
          测试 DataForSEO 连接
        </el-button>
        <el-button
          v-if="$hasPermission('seo.setting', 'edit')"
          size="small"
          :loading="actionLoading.balance"
          @click="onWalletBalance"
        >
          同步钱包余额
        </el-button>
      </div>
      <div class="connection-panel">
        <div class="connection-main">
          <el-tag size="small" :type="connectionTagType">
            {{ connectionStatusText }}
          </el-tag>
          <span class="connection-message">
            {{ connectionInfo.message || "尚未测试 DataForSEO 连接" }}
          </span>
        </div>
        <div class="connection-meta">
          <span>凭证来源：{{ sourceText(connectionInfo.source) }}</span>
          <span>最近测试：{{ formatTime(connectionInfo.tested_at) }}</span>
          <span>接口耗时：{{ formatElapsed(connectionInfo.elapsed_ms) }}</span>
        </div>
      </div>
      <div class="wallet-sync-line">
        钱包最近同步：{{ formatTime(balanceInfo.synced_at) }}
      </div>
      <div class="balance-panel">
        <div class="balance-card">
          <div class="balance-label">钱包余额</div>
          <div class="balance-value">
            {{ balanceInfo.balance === null ? "-" : balanceInfo.balance }}
            <span>{{ balanceInfo.currency }}</span>
          </div>
          <div class="balance-extra">
            最近同步：{{ formatTime(balanceInfo.synced_at) }}
          </div>
        </div>
        <div class="balance-card">
          <div class="balance-label">账户累计</div>
          <div class="balance-value">
            {{ balanceInfo.total === null ? "-" : balanceInfo.total }}
            <span>{{ balanceInfo.currency }}</span>
          </div>
          <div class="balance-extra">来自 DataForSEO user data</div>
        </div>
      </div>
    </el-card>

    <footer-wrap>
      <el-button
        v-if="$hasPermission('seo.setting', 'edit')"
        type="primary"
        size="small"
        @click="onSubmit"
      >
        保 存
      </el-button>
    </footer-wrap>
  </div>
</template>

<script>
import FooterWrap from "@/components/footer-wrap/index.vue";
import {
  apiSeoSettingGet,
  apiSeoSettingSet,
  apiSeoSettingTestConnection,
  apiSeoSettingWalletBalance,
} from "@/api/seo";

export default {
  components: {
    FooterWrap,
  },
  data() {
    return {
      loading: false,
      form: {
        enabled: 0,
        login: "",
        password: "",
        password_configured: 0,
        env_configured: 0,
        monthly_refresh_day: 5,
        monthly_refresh_time: "00:00",
        monthly_timezone: "Asia/Shanghai",
        rank_check_depth: 50,
        rank_depth_mode: "default",
        rank_depth_core: 100,
        rank_depth_long_tail: 50,
        rank_depth_brand: 30,
        rank_depth_competitor: 100,
        standard_batch_size: 100,
        live_manual_enabled: 1,
      },
      actionLoading: {
        test: false,
        balance: false,
      },
      balanceInfo: {
        balance: null,
        total: null,
        currency: "",
        synced_at: 0,
      },
      connectionInfo: {
        status: "",
        source: "",
        message: "",
        elapsed_ms: 0,
        tested_at: 0,
      },
      rules: {
        monthly_refresh_day: [
          { required: true, message: "请选择排名查询日期", trigger: "change" },
        ],
        monthly_refresh_time: [
          { required: true, message: "请选择排名查询时间", trigger: "change" },
        ],
        rank_check_depth: [
          { required: true, message: "请输入默认查询深度", trigger: "blur" },
        ],
        rank_depth_core: [
          { required: true, message: "请输入主词查询深度", trigger: "blur" },
        ],
        rank_depth_long_tail: [
          { required: true, message: "请输入长尾词查询深度", trigger: "blur" },
        ],
        rank_depth_brand: [
          { required: true, message: "请输入品牌词查询深度", trigger: "blur" },
        ],
        rank_depth_competitor: [
          { required: true, message: "请输入竞品词查询深度", trigger: "blur" },
        ],
        standard_batch_size: [
          { required: true, message: "请输入Standard批量数", trigger: "blur" },
        ],
      },
    };
  },
  created() {
    this.getSetting();
  },
  computed: {
    canEdit() {
      return this.$hasPermission("seo.setting", "edit");
    },
    connectionStatusText() {
      if (this.actionLoading.test) return "连接中";
      if (this.connectionInfo.status === "success") return "连接成功";
      if (this.connectionInfo.status === "failed") return "连接失败";
      return "未测试";
    },
    connectionTagType() {
      if (this.actionLoading.test) return "warning";
      if (this.connectionInfo.status === "success") return "success";
      if (this.connectionInfo.status === "failed") return "danger";
      return "info";
    },
  },
  methods: {
    getSetting() {
      this.loading = true;
      apiSeoSettingGet()
        .then((resp) => {
          this.form.enabled = resp.enabled || 0;
          this.form.login = resp.login || "";
          this.form.password = "";
          this.form.password_configured = resp.password_configured || 0;
          this.form.env_configured = resp.env_configured || 0;
          this.form.monthly_refresh_day = resp.monthly_refresh_day || 5;
          this.form.monthly_refresh_time = resp.monthly_refresh_time || "00:00";
          this.form.monthly_timezone = resp.monthly_timezone || "Asia/Shanghai";
          this.form.rank_check_depth = resp.rank_check_depth || 50;
          this.form.rank_depth_mode = resp.rank_depth_mode || "default";
          this.form.rank_depth_core = resp.rank_depth_core || 100;
          this.form.rank_depth_long_tail = resp.rank_depth_long_tail || 50;
          this.form.rank_depth_brand = resp.rank_depth_brand || 30;
          this.form.rank_depth_competitor = resp.rank_depth_competitor || 100;
          this.form.standard_batch_size = resp.standard_batch_size || 100;
          this.form.live_manual_enabled =
            resp.live_manual_enabled === undefined
              ? 1
              : resp.live_manual_enabled;
          this.balanceInfo.balance =
            resp.wallet_balance === undefined || resp.wallet_balance === null
              ? null
              : resp.wallet_balance;
          this.balanceInfo.currency = resp.wallet_balance_currency || "";
          this.balanceInfo.total =
            resp.wallet_total === undefined || resp.wallet_total === null
              ? null
              : resp.wallet_total;
          this.balanceInfo.synced_at = resp.wallet_balance_synced_at || 0;
          this.connectionInfo.status = resp.connection_status || "";
          this.connectionInfo.source = resp.connection_source || "";
          this.connectionInfo.message = resp.connection_status_message || "";
          this.connectionInfo.elapsed_ms = resp.connection_elapsed_ms || 0;
          this.connectionInfo.tested_at = resp.connection_tested_at || 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onSubmit() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) return;
        apiSeoSettingSet({
          enabled: this.form.enabled,
          login: this.form.login,
          password: this.form.password,
          monthly_refresh_day: this.form.monthly_refresh_day,
          monthly_refresh_time: this.form.monthly_refresh_time,
          rank_check_depth: this.form.rank_check_depth,
          rank_depth_mode: this.form.rank_depth_mode,
          rank_depth_core: this.form.rank_depth_core,
          rank_depth_long_tail: this.form.rank_depth_long_tail,
          rank_depth_brand: this.form.rank_depth_brand,
          rank_depth_competitor: this.form.rank_depth_competitor,
          standard_batch_size: 100,
          live_manual_enabled: this.form.live_manual_enabled,
        }).then(() => {
          this.$message({ type: "success", message: "保存成功" });
          this.getSetting();
        });
      });
    },
    onTestConnection() {
      this.actionLoading.test = true;
      this.connectionInfo.status = "";
      this.connectionInfo.message = "正在连接 DataForSEO...";
      apiSeoSettingTestConnection()
        .then((resp) => {
          this.connectionInfo.status = resp.status || "success";
          this.connectionInfo.source = resp.source || "";
          this.connectionInfo.message = resp.status_message || "连接成功";
          this.connectionInfo.elapsed_ms = resp.elapsed_ms || 0;
          this.connectionInfo.tested_at = resp.tested_at || 0;
          this.$message({
            type: "success",
            message: `连接成功，凭证来源：${resp.source || "-"}`,
          });
        })
        .catch((error) => {
          this.connectionInfo.status = "failed";
          this.connectionInfo.message =
            typeof error === "string" ? error : error?.message || "连接失败";
          this.connectionInfo.tested_at = Math.floor(Date.now() / 1000);
        })
        .finally(() => {
          this.actionLoading.test = false;
        });
    },
    onWalletBalance() {
      this.actionLoading.balance = true;
      apiSeoSettingWalletBalance()
        .then((resp) => {
          this.balanceInfo.balance =
            resp.balance === undefined || resp.balance === null
              ? "-"
              : resp.balance;
          this.balanceInfo.currency = resp.currency || "";
          this.balanceInfo.total =
            resp.total === undefined || resp.total === null ? null : resp.total;
          this.balanceInfo.synced_at = resp.synced_at || 0;
          this.$message({ type: "success", message: "钱包余额已同步" });
        })
        .finally(() => {
          this.actionLoading.balance = false;
        });
    },
    formatTime(value) {
      if (!value) return "-";
      const date = new Date(Number(value) * 1000);
      const pad = (num) => String(num).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds()
      )}`;
    },
    formatElapsed(value) {
      if (!value) return "-";
      return `${(Number(value) / 1000).toFixed(2)}秒`;
    },
    sourceText(source) {
      return { env: "环境变量", config: "后台配置" }[source] || "-";
    },
  },
};
</script>

<style lang="scss" scoped>
.form-unit {
  margin-left: 8px;
  color: #606266;
}
.form-prefix {
  margin-right: 8px;
  color: #606266;
}
.form-tips {
  color: #909399;
  font-size: 12px;
  line-height: 22px;
}
.action-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.connection-panel {
  margin-top: 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px 14px;
  background: #fafafa;
}
.connection-main {
  display: flex;
  align-items: center;
}
.connection-message {
  margin-left: 10px;
  color: #606266;
}
.connection-meta {
  display: flex;
  gap: 18px;
  margin-top: 10px;
  color: #909399;
  font-size: 12px;
}
.wallet-sync-line {
  margin-top: 16px;
  color: #606266;
  font-size: 13px;
}
.balance-panel {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}
.balance-card {
  min-width: 220px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 14px 16px;
}
.balance-label {
  color: #909399;
  font-size: 13px;
}
.balance-value {
  margin-top: 8px;
  color: #303133;
  font-size: 24px;
  font-weight: 600;
}
.balance-value span {
  margin-left: 4px;
  color: #606266;
  font-size: 13px;
  font-weight: 400;
}
.balance-extra {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}
</style>
